import { icon, IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { encodeSvgToDataUri } from './encodeSvgToDataUri';

/**
 * FontAwesome v7 icons may draw up to 1/8 of their longest side past each edge of their `viewBox`, for optical alignment with text.
 * Inline, FontAwesome's CSS (`overflow: visible` within a `1.25em` fixed width) keeps that visible, but an image always clips to its `viewBox`.
 */
const OVERFLOW_ALLOWANCE = 1.25;

/**
 * Given a FontAwesome icon definition, this function returns a base64-encoded data URI for the icon's SVG representation.
 *
 * The icon is centered in a square, like the `Icon` component renders it, padded so no part of it is clipped.
 * `scale` is how much of the square the icon's `viewBox` fills, capped at `1 / OVERFLOW_ALLOWANCE` (0.8) to leave room for any overflow.
 */
export default function iconToDataUri(
  iconDefinition: IconDefinition,
  {
    color = 'lightgrey',
    scale = 0.75,
  }: { color?: string; scale?: number } = {}, // ? maybe support SVGAttributes['style'] for more customization if needed
) {
  const [width, height] = iconDefinition.icon;
  const longest = Math.max(width, height);
  const side = Math.max(longest / scale, longest * OVERFLOW_ALLOWANCE);

  const { html } = icon(iconDefinition, {
    // CSS `scale` and `overflow` don't help here; padding the `viewBox` below handles both
    styles: { color },
    attributes: {
      // FontAwesome v7 omits `xmlns`, which an SVG needs to render as an image
      xmlns: 'http://www.w3.org/2000/svg',
    },
  });

  // FontAwesome sets `viewBox` after any `attributes`, so it can only be replaced afterwards; only the root `<svg>` has one
  return encodeSvgToDataUri(
    html
      .join('')
      .replace(
        /viewBox="[^"]*"/,
        `viewBox="${(width - side) / 2} ${(height - side) / 2} ${side} ${side}"`,
      ),
  );
}
